import type { FcmErrorReason } from "@/lib/firebase/fcm";

/**
 * UI-facing status of push registration. Owned by the FcmRegistrationProvider
 * and consumed by the banner / push-status row.
 *
 *  - `unsupported` — no Notification / Service Worker / Push API (terminal).
 *  - `prompt`      — permission is `default`; the banner must request it (no auto-prompt).
 *  - `denied`      — permission `denied` (terminal; manual retry still allowed).
 *  - `offline`     — a real connectivity probe failed (genuinely offline); a backoff
 *                    retry is scheduled, and the `online` event is an additional fast-path.
 *  - `registering` — a token request / backend POST is in flight.
 *  - `registered`  — token successfully POSTed (2xx) for the current key.
 *  - `error`       — transient failure; a backoff retry is scheduled.
 */
export type FcmStatus =
  | "unsupported"
  | "prompt"
  | "denied"
  | "offline"
  | "registering"
  | "registered"
  | "error";

/** Backoff tuning. Exposed for the deterministic unit-reasoning script. */
export const BACKOFF_BASE_MS = 2000;
export const BACKOFF_FACTOR = 2;
export const BACKOFF_CAP_MS = 60_000;

/**
 * A status is terminal when the provider must NOT auto-retry from it. `denied`
 * and `unsupported` require an external change (the user un-blocks notifications
 * / a different browser), so the provider stops its retry loop. A manual
 * `retry()` may still be invoked by the user from these states.
 */
export function isTerminal(status: FcmStatus): boolean {
  return status === "denied" || status === "unsupported";
}

/**
 * Whether an {@link FcmErrorReason} is worth auto-retrying. Transient reasons
 * (offline / network / timeout / unknown) get a backoff retry; terminal reasons
 * (permission / config) do not — they need an external change.
 */
export function isTransientReason(reason: FcmErrorReason): boolean {
  switch (reason) {
    case "offline":
    case "network":
    case "timeout":
    case "unknown":
      return true;
    case "permission":
    case "config":
      return false;
    default: {
      // Exhaustiveness guard: a new reason must be classified explicitly.
      const _exhaustive: never = reason;
      return Boolean(_exhaustive);
    }
  }
}

/**
 * Exponential backoff with jitter for a given (0-based) attempt.
 *
 * delay = min(cap, base + jitter), where base = min(cap, BASE * factor^attempt)
 * and jitter = floor(rand() * base) — i.e. the capped base plus up to 100%
 * additive jitter, re-capped (range [base, 2*base), clamped to cap). `rand` is
 * injectable so the growth / cap behavior can be exercised deterministically in
 * a unit-reasoning script.
 */
export function nextBackoffMs(
  attempt: number,
  rand: () => number = Math.random,
): number {
  const safeAttempt = Number.isFinite(attempt) && attempt > 0 ? attempt : 0;
  const raw = BACKOFF_BASE_MS * Math.pow(BACKOFF_FACTOR, safeAttempt);
  const base = Math.min(BACKOFF_CAP_MS, raw);
  // Up to 100% additive jitter on top of the capped base, re-capped below so we
  // never exceed cap. (Additive — range [base, 2*base) — not classic "full
  // jitter" [0, base); the floor on the base is intentional.)
  const jitter = Math.floor(rand() * base);
  return Math.min(BACKOFF_CAP_MS, base + jitter);
}

/**
 * Feature-detect push support. Pure (reads globals, no side effects). Returns
 * true only when Notification, the Service Worker container, and the Push API
 * are all present.
 */
export function detectSupport(): boolean {
  return (
    typeof Notification !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window
  );
}
